import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DownloadIcon, FileTextIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Etiqueta } from "@/components/ui/etiqueta";
import { Spinner } from "@/components/ui/spinner";
import { getApiErrorMessage } from "@/lib/api";
import { estadoDeLaVistaPrevia, traerBorradorRequest, type CondicionesDeEmision, type Emision } from "@/lib/emision";

interface VistaPreviaDelDocumentoProps {
  informeId: number;
  condiciones: CondicionesDeEmision | undefined;
  emisiones: Emision[];
  /** El formato elegido, para el rótulo de la cabecera. */
  formatoNombre: string | null;
  /** Nombre del archivo al descargar el borrador. */
  nombreDelArchivo: string;
}

/**
 * La vista previa REAL del documento: el PDF de borrador que arma el servidor,
 * pedido a demanda porque tarda segundos. Se muestra embebido desde un `blob:`
 * del propio origen y se puede bajar.
 *
 * La etiqueta dice BORRADOR, o «EMITIDO vN» mientras lo último emitido siga
 * siendo el documento de hoy (nada cambió después).
 */
export function VistaPreviaDelDocumento({
  informeId,
  condiciones,
  emisiones,
  formatoNombre,
  nombreDelArchivo,
}: VistaPreviaDelDocumentoProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El blob vigente vive aquí además del estado, para revocarlo sin depender del render.
  const vigente = useRef<string | null>(null);

  const soltar = useCallback(() => {
    if (vigente.current !== null) URL.revokeObjectURL(vigente.current);
    vigente.current = null;
  }, []);

  // Al salir de la fase o de la pantalla, el blob no se queda retenido.
  useEffect(() => soltar, [soltar]);

  async function generar() {
    setGenerando(true);
    setError(null);

    try {
      const blob = await traerBorradorRequest(informeId);
      soltar();
      vigente.current = URL.createObjectURL(blob);
      setUrl(vigente.current);
    } catch (e) {
      setError(getApiErrorMessage(e, "No pudimos generar la vista previa. Inténtalo de nuevo en un momento."));
    } finally {
      setGenerando(false);
    }
  }

  function descargar() {
    if (url === null) return;

    const enlace = document.createElement("a");
    enlace.href = url;
    enlace.download = nombreDelArchivo;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    toast.success("Descargando la vista previa");
  }

  const etiqueta = estadoDeLaVistaPrevia(condiciones, emisiones);

  return (
    <section aria-label="Vista previa" className="bg-divisor border-border flex min-w-0 flex-col gap-3 rounded-[1.25rem] border-[1.5px] p-5">
      <div className="flex flex-wrap items-center gap-2.5">
        <h3 className="text-ink text-[0.8125rem] font-extrabold">Vista previa</h3>
        <Etiqueta tono={etiqueta.tono === "ok" ? "success" : "warning"}>{etiqueta.texto}</Etiqueta>
        {formatoNombre ? <span className="text-ink-muted ml-auto font-mono text-xs font-bold">{formatoNombre}</span> : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-input h-auto rounded-[0.625rem] px-3 py-1.5 text-xs shadow-none"
          disabled={generando}
          onClick={() => void generar()}
        >
          {generando ? <Spinner aria-hidden /> : <FileTextIcon aria-hidden />}
          {url === null ? "Generar vista previa" : "Volver a generar"}
        </Button>
        {url !== null ? (
          <Button type="button" variant="ghost" size="sm" onClick={descargar}>
            <DownloadIcon aria-hidden />
            Descargar borrador
          </Button>
        ) : null}
        {generando ? (
          <span role="status" className="text-ink-muted text-xs font-semibold">
            Armando el documento. Puede tardar unos segundos.
          </span>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="bg-danger-soft text-danger-texto-fuerte rounded-xl px-3.5 py-3 text-xs font-semibold">
          {error}
        </p>
      ) : null}

      {url !== null ? (
        <iframe
          title="Vista previa del documento del informe"
          src={url}
          className="bg-card border-border h-[48rem] w-full rounded-xl border-[1.5px]"
        />
      ) : (
        <p className="text-meta text-ink-medio bg-card border-border rounded-xl border-[1.5px] px-4 py-10 text-center text-pretty">
          Genera la vista previa para ver el documento tal como está hoy. Lo que falta por validar sale marcado en el
          papel.
        </p>
      )}
    </section>
  );
}
